import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { 
  Container, 
  Box, 
  Button, 
  Typography, 
  Alert,
  Paper
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import { useNavigate } from 'react-router-dom';
import { processImage } from "../../utils/ocr";
import { validateCitizenshipDocument } from "../../utils/validate";

const VerificationPage: React.FC = () => {
  const { user, updateUserProfile } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string>('');
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (selfiePreview) {
        URL.revokeObjectURL(selfiePreview);
      }
    };
  }, [selfiePreview]);

  const handleSelfieChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      if (selfiePreview) {
        URL.revokeObjectURL(selfiePreview);
      }
      const file = e.target.files[0];
      setSelfieFile(file);
      setSelfiePreview(URL.createObjectURL(file));
    }
  };

  const handleFileUpload = async (file: File) => {
    setIsValid(null);
    setExtractedText("Processing...");
    
    try {
      const text = await processImage(file);
      setExtractedText(text);
      
      const isVerified = validateCitizenshipDocument(text);
      setIsValid(isVerified);
      
      if (isVerified && user) {
        await updateUserProfile(user.uid, {
          verificationStatus: 'verified',
          verified: true,
        });
        
        setSuccess(true);
        setTimeout(() => {
          if (selfiePreview) {
            URL.revokeObjectURL(selfiePreview);
          }
          navigate('/');
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button
              onClick={() => navigate('/')}
              variant="outlined"
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Typography variant="h4" align="center" sx={{ flex: 1 }}>
              Citizenship Verification
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>Verification submitted successfully!</Alert>}
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 3,
            mt: 3 
          }}>
            {user?.verificationStatus && (
              <Alert 
                severity={
                  user.verificationStatus === 'verified' ? 'success' : 
                  user.verificationStatus === 'pending' ? 'info' : 
                  user.verificationStatus === 'rejected' ? 'error' : 
                  'info'
                }
                sx={{ width: '100%' }}
              >
                {user.verificationStatus === 'verified' && 'Your identity has been verified!'}
                {user.verificationStatus === 'pending' && 'Your verification is pending review.'}
                {user.verificationStatus === 'rejected' && 'Your verification was rejected. Please try again.'}
                {user.verificationStatus === 'none' && 'Please upload a clear picture of your passport.'}
              </Alert>
            )}

            {selfiePreview ? (
              <Box sx={{ position: 'relative' }}>
                <img 
                  src={selfiePreview} 
                  alt="Verification Selfie Preview" 
                  style={{ 
                    width: '300px', 
                    height: '300px', 
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }} 
                />
              </Box>
            ) : (
              <Box 
                sx={{ 
                  width: '300px', 
                  height: '300px', 
                  bgcolor: 'grey.100', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                }}
              >
                <BadgeIcon sx={{ fontSize: 96, color: 'grey.400' }} />
              </Box>
            )}

            <Button
              component="label"
              variant="outlined"
              startIcon={<BadgeIcon />}
              size="large"
              fullWidth
            >
              Upload Passport
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleSelfieChange}
                capture="user"
              />
            </Button>

            <Typography variant="body2" color="text.secondary" align="center" sx={{ color: 'red' }}>
              Your picture will be deleted after verification.
            </Typography>

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => selfieFile && handleFileUpload(selfieFile)}
              disabled={!selfieFile}
              sx={{ mt: 2, color: 'white' }}
            >
              Submit for Verification
            </Button>

            {extractedText && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Document Verification Results
                </Typography>
                
                {isValid !== null && (
                  <Alert severity={isValid ? "success" : "error"} sx={{ mb: 2 }}>
                    {isValid ? "Valid US citizenship document detected" : "Invalid or unrecognized document"}
                  </Alert>
                )}
                
               
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default VerificationPage; 